import moment from "moment";
import { TFunction } from "i18next";

interface AuditLineArgs {
    insert?: string | null;
    update?: string | null;
    /** Omit when the editor is implicit (e.g. always the logged-in user). */
    editorName?: string | null;
    t: TFunction;
    /** Active app language, so the relative time matches the UI language. */
    language: string;
}

/**
 * Builds a localized "created/updated" audit line, e.g. "Created by Jane today"
 * or "Updated 2 years ago". Uses `insert`/`update` to decide created vs updated
 * (equal → created), relative time ("today" for the current day, otherwise
 * moment.fromNow()), and drops the "by {name}" part when no editorName is given.
 * Returns undefined when there is no date.
 */
export const getAuditLine = ({ insert, update, editorName, t, language }: AuditLineArgs): string | undefined => {
    const isUpdated = !!update && !!insert && update !== insert;
    const date = isUpdated ? update : insert;
    if (!date) return undefined;

    const time = moment(date).isSame(moment(), "day") ? t("today") : moment(date).locale(language).fromNow();

    if (editorName) {
        return t(isUpdated ? "Updated by {{name}} {{time}}" : "Created by {{name}} {{time}}", {
            name: editorName,
            time,
        });
    }
    return t(isUpdated ? "Updated {{time}}" : "Created {{time}}", { time });
};
